/**
 * AssessorEngagements — /w/:slug/app/settings/assessors
 *
 * PR-Cluster-6-a (docs/ux-design-plan.md §10) — Priya persona's
 * admin surface for inviting, revoking, and extending 3PAO
 * engagements. Backend routes shipped in Phase C per
 * docs/uc-2-provenance-api-spec.md §7.9.3.
 *
 * Scope of this first slice:
 *   - List engagements (active + read-only + revoked)
 *   - Invite form: assessor user_id, firm, snapshot_id, window,
 *     archive-window-days picker (30/90/365)
 *   - Revoke button (owner-only server-side; requires typed reason
 *     ≥ 20 chars per EngagementValidationError)
 *   - Extend button (new end date + reason)
 *
 * Deferred:
 *   - Assessor-user picker that resolves user_id from email (needs
 *     new `/api/users/lookup?email=` endpoint)
 *   - Snapshot picker (needs snapshot-storage cluster to ship)
 *   - Engagement-summary PDF affordance per P-C6-13 BH21
 *   - Cross-workspace assessor firm view per P-C6-6
 */

import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, EmptyState, Field, Input, PageHeader, Select, Spinner, Textarea } from '@aigovops/design-system';
import { useOptionalWorkspace } from '../lib/workspace';
import { queryKeys } from '../lib/query-keys';
import {
  extendAssessorEngagement,
  inviteAssessor,
  listAssessorEngagements,
  revokeAssessorEngagement,
  type AssessorEngagementRow,
  type EngagementArchiveWindow,
} from '../lib/api';
import { fmtDate } from '../lib/format';

type EngagementPhase = 'active' | 'read_only' | 'archive_expired' | 'revoked';

function phaseOf(e: AssessorEngagementRow, now: Date = new Date()): EngagementPhase {
  if (e.revoked_at) return 'revoked';
  const nowMs = now.getTime();
  if (nowMs <= Date.parse(e.engagement_end)) return 'active';
  const archiveMs = Date.parse(e.engagement_end) + e.archive_window_days * 24 * 60 * 60 * 1000;
  if (nowMs <= archiveMs) return 'read_only';
  return 'archive_expired';
}

export function AssessorEngagements() {
  const workspaceCtx = useOptionalWorkspace();
  const slug = workspaceCtx?.workspace.slug ?? null;
  const canManage = workspaceCtx?.role === 'owner';

  // A Blueprint export ("Deliver to assessor portal") links here with the
  // exported version_id pre-filled as the snapshot to engage on.
  const [searchParams] = useSearchParams();
  const prefillSnapshotId = searchParams.get('snapshot_id') ?? '';

  const [error, setError] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(prefillSnapshotId.length > 0);

  const engagementsQuery = useQuery({
    queryKey: queryKeys.assessorEngagements(slug ?? ''),
    queryFn: () => listAssessorEngagements(slug!),
    enabled: !!slug,
  });
  const engagements = {
    data: engagementsQuery.data ?? null,
    error: engagementsQuery.error?.message ?? null,
    loading: engagementsQuery.isLoading,
  };

  const refresh = useCallback(() => { engagementsQuery.refetch(); }, [engagementsQuery]);

  if (!slug) return null;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-5)', maxWidth: '72rem' }}>
      <PageHeader
        title="3PAO assessor engagements"
        subtitle="Invite an assessor into this workspace, scoped to one snapshot."
        right={
          canManage ? (
            <Button variant="primary" compact onClick={() => setShowInvite((s) => !s)}>
              {showInvite ? 'Cancel' : '+ Invite assessor'}
            </Button>
          ) : null
        }
      />

      {error ? <Alert variant="error">{error}</Alert> : null}
      {!canManage ? (
        <Alert variant="info">
          Only workspace owners can invite or revoke assessor engagements. You can view existing engagements below.
        </Alert>
      ) : null}

      {showInvite && canManage ? (
        <InviteForm
          slug={slug}
          initialSnapshotId={prefillSnapshotId}
          onDone={() => {
            setShowInvite(false);
            refresh();
          }}
          onError={setError}
          onCancel={() => setShowInvite(false)}
        />
      ) : null}

      {engagements.loading ? (
        <Spinner label="Loading engagements" />
      ) : engagements.error ? (
        <Alert variant="error">{engagements.error}</Alert>
      ) : !engagements.data || engagements.data.engagements.length === 0 ? (
        <EmptyState
          title="No assessor engagements"
          description="Invite an assessor here when your 3PAO is ready to begin fieldwork."
          action={
            canManage ? (
              <Button variant="primary" onClick={() => setShowInvite(true)}>
                + Invite assessor
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
          {engagements.data.engagements.map((e) => (
            <EngagementCard
              key={e.engagement_id}
              slug={slug}
              engagement={e}
              canManage={canManage}
              onError={setError}
              onChanged={refresh}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Invite form -----------------------------------------------------

function InviteForm(props: {
  slug: string;
  initialSnapshotId?: string;
  onDone: () => void;
  onError: (msg: string) => void;
  onCancel: () => void;
}) {
  const [assessorUserId, setAssessorUserId] = useState('');
  const [firm, setFirm] = useState('');
  const [snapshotId, setSnapshotId] = useState(props.initialSnapshotId ?? '');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [archiveWindow, setArchiveWindow] = useState<EngagementArchiveWindow>(90);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await inviteAssessor(props.slug, {
        assessor_user_id: assessorUserId.trim(),
        assessor_firm: firm.trim(),
        snapshot_id: snapshotId.trim(),
        engagement_start: new Date(start).toISOString(),
        engagement_end: new Date(end).toISOString(),
        archive_window_days: archiveWindow,
      });
      props.onDone();
    } catch (err) {
      props.onError(err instanceof Error ? err.message : 'Invite failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      style={{
        display: 'grid',
        gap: 'var(--space-3)',
        padding: 'var(--space-4) var(--space-5)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
      }}
    >
      <h2 style={{ margin: 0, fontSize: 'var(--font-size-md)' }}>Invite assessor</h2>
      <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)' }}>
        The assessor gets a read-only session scoped to the snapshot you pin. Q&amp;A, evidence
        requests, and annotations are the only write paths available to them.
      </p>
      <div style={{ display: 'grid', gap: 'var(--space-3)', gridTemplateColumns: 'repeat(auto-fit, minmax(18rem, 1fr))' }}>
        <Field id="assessor-user-id" label="Assessor user_id">
          <Input
            id="assessor-user-id"
            required
            value={assessorUserId}
            onChange={(e) => setAssessorUserId(e.target.value)}
            placeholder="user_..."
            autoComplete="off"
          />
        </Field>
        <Field id="assessor-firm" label="Assessor firm">
          <Input
            id="assessor-firm"
            required
            value={firm}
            onChange={(e) => setFirm(e.target.value)}
            placeholder="e.g. Coalfire · Schellman · Kratos"
            autoComplete="off"
          />
        </Field>
      </div>
      <Field id="assessor-snapshot" label="Snapshot id (immutable point-in-time)">
        <Input
          id="assessor-snapshot"
          required
          value={snapshotId}
          onChange={(e) => setSnapshotId(e.target.value)}
          placeholder="snap_..."
          autoComplete="off"
        />
      </Field>
      <div style={{ display: 'grid', gap: 'var(--space-3)', gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))' }}>
        <Field id="assessor-start" label="Engagement start">
          <Input
            id="assessor-start"
            type="date"
            required
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
        </Field>
        <Field id="assessor-end" label="Engagement end">
          <Input
            id="assessor-end"
            type="date"
            required
            value={end}
            onChange={(e) => setEnd(e.target.value)}
          />
        </Field>
        <Field id="assessor-archive-window" label="Archive window">
          <Select
            id="assessor-archive-window"
            value={String(archiveWindow)}
            onChange={(e) => setArchiveWindow(Number(e.target.value) as EngagementArchiveWindow)}
          >
            <option value="30">30 days</option>
            <option value="90">90 days</option>
            <option value="365">365 days</option>
          </Select>
        </Field>
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
        <Button variant="secondary" compact type="button" onClick={props.onCancel}>
          Cancel
        </Button>
        <Button variant="primary" compact type="submit" disabled={busy}>
          {busy ? 'Inviting…' : 'Send invite'}
        </Button>
      </div>
    </form>
  );
}

// ---- Engagement card -------------------------------------------------

function EngagementCard(props: {
  slug: string;
  engagement: AssessorEngagementRow;
  canManage: boolean;
  onError: (msg: string) => void;
  onChanged: () => void;
}) {
  const { engagement: e } = props;
  const phase = phaseOf(e);
  const [showRevoke, setShowRevoke] = useState(false);
  const [showExtend, setShowExtend] = useState(false);

  return (
    <section
      aria-label={`Engagement ${e.engagement_id}`}
      style={{
        border: '1px solid var(--border)',
        borderLeftWidth: '3px',
        borderLeftColor: phaseBorder(phase),
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-4) var(--space-5)',
        display: 'grid',
        gap: 'var(--space-3)',
      }}
    >
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <div style={{ display: 'grid', gap: '4px' }}>
          <h3 style={{ margin: 0, fontSize: 'var(--font-size-md)', fontWeight: 600 }}>{e.assessor_firm}</h3>
          <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }} title={e.assessor_user_id}>
            {e.assessor_user_id}
          </span>
        </div>
        <PhasePill phase={phase} />
      </header>

      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(11rem, 1fr))',
          gap: 'var(--space-2) var(--space-4)',
          margin: 0,
          fontSize: 'var(--font-size-3xs)',
        }}
      >
        <MetaRow label="Snapshot">
          <code style={{ fontFamily: 'var(--font-mono)', color: 'var(--text)' }} title={e.snapshot_id}>
            {e.snapshot_id.length > 24 ? `${e.snapshot_id.slice(0, 24)}…` : e.snapshot_id}
          </code>
        </MetaRow>
        <MetaRow label="Engagement window">
          <time dateTime={e.engagement_start}>{fmtDate(e.engagement_start)}</time>
          <span aria-hidden style={{ color: 'var(--text-dim)' }}> → </span>
          <time dateTime={e.engagement_end}>{fmtDate(e.engagement_end)}</time>
        </MetaRow>
        <MetaRow label="Archive window">{e.archive_window_days} days after end</MetaRow>
        <MetaRow label="Invited by">
          <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }} title={e.invited_by}>
            {e.invited_by}
          </span>
          <span aria-hidden style={{ color: 'var(--text-dim)' }}> · </span>
          <time dateTime={e.invited_at}>{fmtDate(e.invited_at)}</time>
        </MetaRow>
        {e.revoked_at ? (
          <MetaRow label="Revoked">
            <time dateTime={e.revoked_at}>{fmtDate(e.revoked_at)}</time>
            {e.revoked_reason ? (
              <div style={{ marginTop: 4, color: 'var(--text-muted)', fontStyle: 'italic', fontSize: 'var(--font-size-3xs)' }}>
                “{e.revoked_reason}”
              </div>
            ) : null}
          </MetaRow>
        ) : null}
      </dl>

      {props.canManage && phase !== 'revoked' && phase !== 'archive_expired' ? (
        <div style={{ display: 'flex', gap: 'var(--space-2)', paddingTop: 'var(--space-2)', borderTop: '1px solid var(--border)' }}>
          <Button variant="ghost" compact onClick={() => setShowExtend((s) => !s)}>
            {showExtend ? 'Cancel extend' : 'Extend engagement'}
          </Button>
          <div style={{ flex: 1 }} />
          <Button variant="ghost" compact onClick={() => setShowRevoke((s) => !s)}>
            {showRevoke ? 'Cancel revoke' : 'Revoke engagement'}
          </Button>
        </div>
      ) : null}

      {showExtend ? (
        <ExtendForm
          slug={props.slug}
          engagement={e}
          onDone={() => {
            setShowExtend(false);
            props.onChanged();
          }}
          onCancel={() => setShowExtend(false)}
          onError={props.onError}
        />
      ) : null}
      {showRevoke ? (
        <RevokeForm
          slug={props.slug}
          engagement={e}
          onDone={() => {
            setShowRevoke(false);
            props.onChanged();
          }}
          onCancel={() => setShowRevoke(false)}
          onError={props.onError}
        />
      ) : null}
    </section>
  );
}

function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt
        style={{
          fontSize: 'var(--font-size-3xs)',
          color: 'var(--text-dim)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontWeight: 600,
          margin: 0,
        }}
      >
        {label}
      </dt>
      <dd style={{ margin: 0, color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }}>{children}</dd>
    </div>
  );
}

function PhasePill({ phase }: { phase: EngagementPhase }) {
  const tone: Record<EngagementPhase, { bg: string; fg: string; label: string }> = {
    active: { bg: 'var(--success-bg)', fg: 'var(--success-fg)', label: 'Active' },
    read_only: { bg: 'var(--warning-bg)', fg: 'var(--warning-fg)', label: 'Read-only (archive)' },
    archive_expired: { bg: 'var(--surface-alt)', fg: 'var(--text-muted)', label: 'Expired' },
    revoked: { bg: 'var(--danger-bg)', fg: 'var(--danger-fg)', label: 'Revoked' },
  };
  const t = tone[phase];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        background: t.bg,
        color: t.fg,
      }}
    >
      {t.label}
    </span>
  );
}

function phaseBorder(phase: EngagementPhase): string {
  switch (phase) {
    case 'active':
      return 'var(--band-green)';
    case 'read_only':
      return 'var(--band-amber)';
    case 'archive_expired':
      return 'var(--border)';
    case 'revoked':
      return 'var(--band-red)';
  }
}

// ---- Revoke + Extend forms ------------------------------------------

function RevokeForm(props: {
  slug: string;
  engagement: AssessorEngagementRow;
  onDone: () => void;
  onCancel: () => void;
  onError: (msg: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await revokeAssessorEngagement(props.slug, props.engagement.engagement_id, reason.trim());
      props.onDone();
    } catch (err) {
      props.onError(err instanceof Error ? err.message : 'Revoke failed.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      onSubmit={submit}
      style={{
        display: 'grid',
        gap: 'var(--space-2)',
        padding: 'var(--space-3) var(--space-4)',
        background: 'var(--danger-bg)',
        border: '1px solid var(--band-red)',
        borderLeftWidth: '3px',
        borderRadius: 'var(--radius-card)',
      }}
    >
      <strong style={{ color: 'var(--danger-fg)', fontWeight: 700 }}>Revoke assessor engagement</strong>
      <p style={{ margin: 0, fontSize: 'var(--font-size-3xs)', color: 'var(--danger-fg)' }}>
        The assessor's session is terminated immediately. Reason ≥ 20 chars, chained into the audit log.
      </p>
      <Field id="revoke-reason" label="Reason (audit-logged)">
        <Textarea
          id="revoke-reason"
          required
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Field>
      <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
        <Button variant="secondary" compact type="button" onClick={props.onCancel}>
          Cancel
        </Button>
        <Button variant="primary" compact type="submit" disabled={busy || reason.trim().length < 20}>
          {busy ? 'Revoking…' : 'Confirm revoke'}
        </Button>
      </div>
    </form>
  );
}

function ExtendForm(props: {
  slug: string;
  engagement: AssessorEngagementRow;
  onDone: () => void;
  onCancel: () => void;
  onError: (msg: string) => void;
}) {
  const [newEnd, setNewEnd] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await extendAssessorEngagement(props.slug, props.engagement.engagement_id, {
        new_engagement_end: new Date(newEnd).toISOString(),
        reason: reason.trim(),
      });
      props.onDone();
    } catch (err) {
      props.onError(err instanceof Error ? err.message : 'Extend failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      style={{
        display: 'grid',
        gap: 'var(--space-2)',
        padding: 'var(--space-3) var(--space-4)',
        background: 'var(--warning-bg)',
        border: '1px solid var(--band-amber)',
        borderLeftWidth: '3px',
        borderRadius: 'var(--radius-card)',
      }}
    >
      <strong style={{ color: 'var(--warning-fg)', fontWeight: 700 }}>Extend engagement</strong>
      <p style={{ margin: 0, fontSize: 'var(--font-size-3xs)', color: 'var(--warning-fg)' }}>
        Pushes engagement_end forward. Must be strictly after the current end date.
      </p>
      <div style={{ display: 'grid', gap: 'var(--space-2)', gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))' }}>
        <Field id="extend-end" label="New engagement end">
          <Input
            id="extend-end"
            type="date"
            required
            value={newEnd}
            onChange={(e) => setNewEnd(e.target.value)}
            min={props.engagement.engagement_end.slice(0, 10)}
          />
        </Field>
        <Field id="extend-reason" label="Reason (audit-logged)">
          <Input id="extend-reason" required value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
        <Button variant="secondary" compact type="button" onClick={props.onCancel}>
          Cancel
        </Button>
        <Button variant="primary" compact type="submit" disabled={busy}>
          {busy ? 'Extending…' : 'Confirm extend'}
        </Button>
      </div>
    </form>
  );
}
